import * as React from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { WarningOutlined, HomeOutlined } from '@ant-design/icons';
import { theme } from '../utils/theme';

const ErrorContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  z-index: 9999;
  pointer-events: auto;
`;

const ErrorIcon = styled.div`
  font-size: 80px;
  margin-bottom: 24px;
  color: #faad14;
`;

const ErrorTitle = styled.h2`
  font-size: 20px;
  font-weight: 500;
  color: #262626;
  margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 14px;
  color: ${theme.neutral.text.secondary};
  margin: 0 0 32px 0;
  line-height: 1.6;
  max-width: 320px;
`;

const ErrorDetails = styled.details`
  margin-top: 32px;
  padding: 12px 16px;
  background: ${theme.neutral.bg.secondary};
  border-radius: 4px;
  width: 100%;
  max-width: 400px;
  text-align: left;
  font-size: 12px;
  color: #666;
  white-space: pre-wrap;
  word-break: break-word;

  summary {
    cursor: pointer;
    font-weight: 500;
    color: ${theme.brand.primary};
    user-select: none;
    margin-bottom: 8px;
  }
`;

export const ErrorFallback = ({ error, componentStack, resetError }) => {
  const handleRefresh = () => {
    if (resetError) {
      resetError();
    }
    window.location.href = '/';
  };

  const isDevelopment =
    typeof process !== 'undefined' && process.env.NODE_ENV === 'development';

  return (
    <ErrorContainer>
      <ErrorIcon>
        <WarningOutlined />
      </ErrorIcon>
      <ErrorTitle>Došlo je do greške</ErrorTitle>
      <ErrorMessage>
        Nešto nije u redu. Pokušajte se vratiti na početnu stranicu ili
        restartujte aplikaciju.
      </ErrorMessage>
      <Button
        type="primary"
        size="large"
        icon={<HomeOutlined />}
        onClick={handleRefresh}
        style={{ width: '100%', maxWidth: '320px' }}
      >
        Vrati se na početnu
      </Button>
      {isDevelopment && error && (
        <ErrorDetails>
          <summary>Tehnički detalji (dev)</summary>
          <div>
            <strong>Error:</strong>
            <div style={{ marginTop: '4px', color: '#f5222d' }}>
              {error.toString()}
            </div>
            {componentStack && (
              <>
                <br />
                <strong>Component Stack:</strong>
                <div style={{ marginTop: '4px', fontSize: '11px' }}>
                  {componentStack}
                </div>
              </>
            )}
          </div>
        </ErrorDetails>
      )}
    </ErrorContainer>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  resetError = () => {
    this.setState({ error: null, errorInfo: null });
  };

  render() {
    if (this.state.errorInfo) {
      return (
        <ErrorFallback
          error={this.state.error}
          componentStack={this.state.errorInfo.componentStack}
          resetError={this.resetError}
        />
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
